import {afterNextRender, Component, DestroyRef, inject, Injector, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {selectFundPartnershipInvestment, selectIsAnyFundFormInEditMode, selectRelatedDealId, selectSelectedFundId} from "../../store/fund/fund.selectors";
import {Store} from "@ngrx/store";
import {MatDialog, MatDialogConfig} from "@angular/material/dialog";
import {FundInvestmentNewDialogComponent} from "../fund-investment-new-dialog/fund-investment-new-dialog.component";
import {CodeTableEnum} from "../../../shared/model/code";
import {EarningBaseCode} from "../../../shared/model/earning-base-code";
import {selectFundReportHasNegativeUndrawn, selectSelectedFundReport} from "../../store/fund-report/fund-report.selectors";
import {selectSelectedFundValuationId} from "../../store/fund-valuation/fund-valuation.selectors";
import {selectSelectedFundInvestmentsForFundReport} from "../../store/fund-investment/fund-investment.selectors";
import {BehaviorSubject, filter, map, Subscription, take} from "rxjs";
import {FundActions} from "../../store/fund.actions";
import {DateUtil} from "../../../shared/utils/date-util";
import {TraceableDate, TraceableMoney, TraceableText} from "../../../shared/model/traceable";
import {AssetDetailsDialogComponent, assetDialogExpandType} from "./asset-details-dialog/asset-details-dialog.component";
import {MatSort} from "@angular/material/sort";
import {Router} from "@angular/router";
import {AssetValuationTypeEnum} from "../../../asset/models/asset-valuation/asset-valuation-type.enum";
import {MatTableDataSource} from "@angular/material/table";
import {PartnershipInvestmentRecord} from "../../models/partnership-investment-record";
import {needsWriteoffReview} from "../../models/writeoff-flag-state";
import {ConfirmationComponent} from "../../../shared/components/confirmation/confirmation.component";
import {AssetValuationWriteoffActions} from "../../../asset/store/asset.actions";
import {TableSettingsDialogComponent, TableSettingsDialogData, TableSettingsDialogResult} from "../../../shared/user-setting/table-settings-dialog/table-settings-dialog.component";
import {selectSettingsLoaded, selectTableColumns, selectTablePageSize, selectTableSort} from "../../../shared/user-setting/store/user-setting.selectors";
import {UserSettingActions} from "../../../shared/user-setting/store/user-setting.actions";
import {TableSort} from "../../../shared/generated-sources/api/user-setting";
import {
    COMMON_COLUMNS,
    deriveEmptyFooterCells,
    GP_REPORT_COLUMNS,
    SCENARIO_TOKEN_MEMBERS,
    SCENARIO_TOKENS_ORDER,
    STICKY_FOOTER_LABELS,
    VALUATION_COLUMNS
} from "./partnership-investment-columns";

@Component({
    selector: "valumize-fund-partnership-investments",
    templateUrl: "./fund-partnership-investments.component.html",
    styleUrls: ["./fund-partnership-investments.component.scss"],
    standalone: false
})
export class FundPartnershipInvestmentsComponent implements OnInit, OnDestroy {

    private sortRef?: MatSort;
    private defaultApplied = false;

    @ViewChild("partnershipInvestmentSort", {read: MatSort})
    set partnershipInvestmentSortSetter(s: MatSort | undefined) {
        this.sortRef = s;
        this.scheduleWireSort();
    }

    private readonly store = inject(Store);
    private readonly dialog = inject(MatDialog);
    private readonly router = inject(Router);
    private readonly injector = inject(Injector);
    private readonly destroyRef = inject(DestroyRef);

    private scheduleWireSort() {
        afterNextRender(() => this.wireSort(), {injector: this.injector});
    }

    subscriptions: Subscription[] = [];

    fundId$ = this.store.select(selectSelectedFundId);
    selectedFundReport$ = this.store.select(selectSelectedFundReport);
    partnershipInvestments$ = this.store.select(selectFundPartnershipInvestment);
    hasNegativeUndrawn$ = this.store.select(selectFundReportHasNegativeUndrawn);
    selectedFundInvestmentsForFundReport$ = this.store.select(selectSelectedFundInvestmentsForFundReport);
    isEditDisabled$ = this.store.select(selectIsAnyFundFormInEditMode);

    dealId?: number;
    fundValuationId?: number;
    fundReportDate?: TraceableDate;
    fundClosingDate?: TraceableDate;
    panelOpenState = false;

    codeTableCountry = CodeTableEnum.SHARED_COUNTRY;
    codeTableGic = CodeTableEnum.SHARED_GICS;
    codeTableGpIndication = CodeTableEnum.ASSET_GPINDICATION;
    codeTableAssessment = CodeTableEnum.SHARED_ASSESSMENT;

    partnershipInvestmentDataSource = new MatTableDataSource<PartnershipInvestmentRecord>();

    // One source of truth for column order (helps with future column reordering)
    displayedColumns$ = new BehaviorSubject<string[]>([]);

    // footer row models (one per “sticky” row)
    footerTUA?: PartnershipInvestmentRecord;   // Total Of Unrealized Assets
    footerNCA?: PartnershipInvestmentRecord;   // Net Current Assets
    footerGPCR?: PartnershipInvestmentRecord;  // GP Carry Reserve
    footerBPA?: PartnershipInvestmentRecord;   // Bid Price Adjustment
    footerTOTAL?: PartnershipInvestmentRecord; // Total

    // footer column-id arrays (same order as displayedColumns, with a suffix)
    footerColsTUA: string[] = [];
    footerColsNCA: string[] = [];
    footerColsGPCR: string[] = [];
    footerColsBPA: string[] = [];
    footerColsTOTAL: string[] = [];

    private readonly withSuffix = (cols: string[], suffix: string) => cols.map(c => `${c}__${suffix}`);

    stickyNames: ReadonlySet<string> = new Set(Object.values(STICKY_FOOTER_LABELS));

    protected readonly assetDialogExpandType = assetDialogExpandType;

    // US-1189: "no forecast cashflow — is this a write-off?" prompt in the sticky company cell.
    protected readonly needsWriteoffReview = needsWriteoffReview;

    /**
     * US-1189: asks the user to confirm that an asset without any forecast cashflow really is a write-off.
     * A write-off is never set as a free-standing property — this only records the answer to a condition the
     * backend detected. Declining stores nothing, so the icon stays as an open to-do.
     */
    confirmWriteoff(record: PartnershipInvestmentRecord) {
        // Footer/summary and manually-added rows carry no valuation, so there is nothing to acknowledge.
        if (record.assetValuationId === undefined) {
            return;
        }

        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = `Is ${record.company.text ?? "this asset"} a write-off?`;
        dialogRef.componentInstance.htmlContent =
            "No forecast cashflow is modelled for this asset. Confirm that no proceeds are expected, "
            + "or cancel and enter a forecast instead.";
        dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
            if (confirmed) {
                this.store.dispatch(AssetValuationWriteoffActions.confirm({
                    assetId: record.assetId,
                    assetValuationId: record.assetValuationId!,
                    confirmed: true
                }));
            }
        });
    }

    /** Derived from COLUMN_DATA_FOOTERS in partnership-investment-columns.ts —
     *  the complement of "columns × suffixes that have a data <td>". Each entry
     *  is rendered as an empty `<td>` so the row always covers every visible column. */
    readonly emptyFooterCells: string[] = deriveEmptyFooterCells();

    isScenarioStartFooterCell(cellId: string): boolean {
        return this.isScenarioStart(cellId.split("__")[0]);
    }

    isGroupStartFooterCell(cellId: string): boolean {
        return this.groupStartCols.has(cellId.split("__")[0]);
    }

    constructor() {
    }

    ngOnInit() {
        this.subscriptions.push(this.store.select(selectSelectedFundValuationId).pipe(map(fundValuationId => {
            this.fundValuationId = fundValuationId;
        })).subscribe());

        this.subscriptions.push(this.store.select(selectRelatedDealId).pipe(map(dealId => {
            this.dealId = dealId;
        })).subscribe());

        this.subscriptions.push(this.store.select(selectFundPartnershipInvestment).pipe(map((partnershipInvestment) => {
            this.fundReportDate = partnershipInvestment.fundReportDate;
            this.fundClosingDate = partnershipInvestment.fundClosingDate;

            this.partnershipInvestmentDataSource.data = partnershipInvestment.tableDatasource.filter(r => !this.stickyNames.has(r.company.text!));

            this.footerTUA = partnershipInvestment.footerByName?.get(STICKY_FOOTER_LABELS.TUA);
            this.footerNCA = partnershipInvestment.footerByName?.get(STICKY_FOOTER_LABELS.NCA);
            this.footerGPCR = partnershipInvestment.footerByName?.get(STICKY_FOOTER_LABELS.GPCR);
            this.footerBPA = partnershipInvestment.footerByName?.get(STICKY_FOOTER_LABELS.BPA);
            this.footerTOTAL = partnershipInvestment.footerByName?.get(STICKY_FOOTER_LABELS.TOTAL);

            this.initColumnPicker(partnershipInvestment.availableColumns, partnershipInvestment.defaultTokens);
            this.scheduleWireSort();
        })).subscribe());

        this.partnershipInvestmentDataSource.sortingDataAccessor = (row, column) => {
            switch (column) {
                case "company":
                    return row.company.text;
                case "activityIndustry":
                    return row.activityIndustry.text;
                case "country":
                    return row.country.code;
                case "gic":
                    return row.gic.code;
                case "gpIndication":
                    return row.gpIndication.code;
                case "investmentDate":
                    return row.investmentDate.date;

                case "percentageHeld":
                    return row.percentageHeld.fraction;
                case "remainingCost":
                    return row.remainingCost.amount;
                case "gpNAV":
                    return row.gpNAV.amount;
                case "unrealizedTVPI":
                    return row.unrealizedTVPI.factor;
                case "totalTVPI":
                    return row.totalTVPI.factor;

                case "exitDateAssumption":
                    return row.exitDateAssumption.date;
                case "valuationType":
                    return row.preferredValuationType.code;
                case "discountRate":
                    return row.discountRate.fraction;
                case "selectedBid":
                    return row.selectedBid.amount;
                case "discountToNAV":
                    return row.discountToNAV.fraction;
                case "percentageOfTotalBid":
                    return row.percentageOfTotalBid.fraction;
                case "percentageOfTotalNAV":
                    return row.percentageOfTotalNAV.fraction;
                case "percentageOfProceeds":
                    return row.percentageOfProceeds?.fraction;
                case "evOverEbitda":
                    return row.evOverEbitda?.factor;
                case "evOverRevenue":
                    return row.evOverRevenue?.factor;
                case "impliedTrading":
                    return row.impliedTrading?.gpEvEbitda;
                case "ebitdaMargin":
                    return row.ebitdaMargin?.fraction;
                case "revenueCAGR":
                    return row.revenueCAGR?.fraction;
                case "marginExpansion":
                    return row.marginExpansion?.fraction;
                case "weightedRemainingHold":
                    return row.weightedRemainingHold?.text;

                case "stabilityOfCFs":
                    return row.stabilityOfCFs.code;

                case "realizedTVPI":
                    return row.realizedTVPI.factor;
                case "realizedCost":
                    return row.realizedCost.amount;
                case "realizedGains":
                    return row.realizedGains.amount;
                case "totalRealized":
                    return row.totalRealized.amount;
                case "totalCost":
                    return row.totalCost.amount;
                case "mezzanine":
                    return row.mezzanine.amount;
                case "preferredEquity":
                    return row.preferredEquity.amount;
                case "commonEquity":
                    return row.commonEquity.amount;
                case "totalIRR":
                    return row.totalIRR.fraction;
                case "realizedPercentageOfInvestment":
                    return row.realizedPercentageOfInvestment.fraction;
                case "committedInvestment":
                    return row.committedInvestment.amount;
                case "capexDrawdown":
                    return row.capexDrawdown.amount;

                case "cashflowLow":
                    return row.cashflowLow.amount;
                case "cashflowBase":
                    return row.cashflowBase.amount;
                case "cashflowHigh":
                    return row.cashflowHigh.amount;

                case "navMultipleLow":
                    return row.navMultipleLow.factor;
                case "primaryTVPILow":
                    return row.primaryTVPILow.factor;
                case "secondaryTVPILow":
                    return row.secondaryTVPILow.factor;
                case "secondaryIRRLow":
                    return row.secondaryIRRLow.fraction;

                case "navMultipleBase":
                    return row.navMultipleBase.factor;
                case "primaryTVPIBase":
                    return row.primaryTVPIBase.factor;
                case "secondaryTVPIBase":
                    return row.secondaryTVPIBase.factor;
                case "secondaryIRRBase":
                    return row.secondaryIRRBase.fraction;

                case "navMultipleHigh":
                    return row.navMultipleHigh.factor;
                case "primaryTVPIHigh":
                    return row.primaryTVPIHigh.factor;
                case "secondaryTVPIHigh":
                    return row.secondaryTVPIHigh.factor;
                case "secondaryIRRHigh":
                    return row.secondaryIRRHigh.fraction;

                default:
                    return (row as any)[column];
            }
        };
    }

    private static readonly MAX_WIRE_SORT_RETRIES = 5;
    private wireSortRetries = 0;

    private wireSort() {
        const s = this.sortRef;
        if (!s) {
            this.wireSortRetries = 0;
            return;
        }

        // Attach (idempotent)
        this.partnershipInvestmentDataSource.sort = s;

        // If headers not registered yet, retry after the next render once MatSortHeader directives
        // have registered. Bounded retries protect against pathological cases (e.g. column-list
        // races) where sortables would never populate; in normal flow it converges in 1–2 ticks.
        if (s.sortables.size === 0) {
            if (this.wireSortRetries < FundPartnershipInvestmentsComponent.MAX_WIRE_SORT_RETRIES) {
                this.wireSortRetries++;
                this.scheduleWireSort();
            }
            return;
        }
        this.wireSortRetries = 0;

        // Preserve active if still valid; otherwise use savedSort or default to 'company' ONCE
        const hasValidActive = !!s.active && s.sortables.has(s.active);
        if (!hasValidActive && !this.defaultApplied) {
            const target: TableSort = this.savedSort && s.sortables.has(this.savedSort.field)
                ? this.savedSort
                : {field: "company", direction: "asc"};
            if (s.sortables.has(target.field)) {
                s.sort({id: target.field, start: target.direction, disableClear: true});
                this.defaultApplied = true;
            }
        }
    }

    getDisplayScenarioValue(scenarioValue: TraceableMoney, ncaValue: TraceableMoney, assetId: number, rowName: TraceableText): TraceableMoney {
        if (assetId < 0 && rowName.text === "Net Current Assets") {
            return scenarioValue.amount !== null ? scenarioValue : ncaValue;
        }
        return scenarioValue;
    }

    displayValuationType(valuationType: string): string {
        if (valuationType === AssetValuationTypeEnum.FinancialOperationalValuation) {
            return "Operational";
        } else if (valuationType === AssetValuationTypeEnum.DiscountMultipleValuation) {
            return "Discount";
        }
        return "-";
    }

    /**
     * Discount-multiple valuations don't carry forecast data (no exit-record
     * extrapolation, no revenue CAGR, no margin expansion). Display "n.a." for
     * any forecast-driven cell of such records.
     */
    isDiscountMultipleValuation(record: PartnershipInvestmentRecord): boolean {
        return record.preferredValuationType?.code === AssetValuationTypeEnum.DiscountMultipleValuation;
    }

    /**
     * Earning-base star indicator: shown next to evOverEbitda when the asset's
     * earning base is anything other than EBITDA (e.g. Revenue, EBIT) — AND only
     * for valuations that carry forecast data.
     */
    showEarningBaseStar(record: PartnershipInvestmentRecord): boolean {
        return !this.isDiscountMultipleValuation(record)
            && !!record.earningBase?.code
            && record.earningBase.code !== EarningBaseCode.EBITDA;
    }

    styleRowExited(row: any): string {
        const exitDateAssumption = DateUtil.fromIsoDate(row.exitDateAssumption.date);
        const fundReportDate = DateUtil.fromIsoDate(this.fundReportDate?.date);
        const fundClosingDate = DateUtil.fromIsoDate(this.fundClosingDate?.date);
        if (!fundReportDate || !fundClosingDate || !exitDateAssumption) {
            return "";
        }
        return exitDateAssumption > fundReportDate && exitDateAssumption < fundClosingDate ? "exited" : "";
    }

    setBackgroundColor(stabilityOfCFs: string): string {
        switch (stabilityOfCFs) {
            case "LOW":
                return "stability-of-cfs low";
            case "MEDIUM":
                return "stability-of-cfs medium";
            case "HIGH":
                return "stability-of-cfs high";
            default:
                return "";
        }
    }

    openDialog(element: PartnershipInvestmentRecord, fundId: number, fundReportId: number, expanded: assetDialogExpandType) {
        this.openAssetDMVValuationDialog(
            element.assetId,
            element.preferredValuationType.code,
            fundId,
            fundReportId,
            this.fundValuationId,
            element.fundInvestmentId,
            expanded
        );
    }

    openManageFundInvestmentsDialog(fundId: number, fundReportId: number) {
        const dialogRef = this.dialog.open(FundInvestmentNewDialogComponent);
        dialogRef.componentInstance.fundId = fundId;
        dialogRef.componentInstance.fundReportId = fundReportId;
        dialogRef.componentInstance.fundValuationId = this.fundValuationId;
    }

    openAssetDMVValuationDialog(
        assetId: number,
        preferredValuationType: string | undefined,
        fundId: number,
        fundReportId: number,
        fundValuationId: number | undefined,
        fundInvestmentId: number,
        expanded: assetDialogExpandType
    ) {
        const valuationTypeEnum = Object.values(AssetValuationTypeEnum).find(enumValue => enumValue === preferredValuationType);

        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = "1400px";

        const dialogDMVRef = this.dialog.open(AssetDetailsDialogComponent, dialogConfig);
        dialogDMVRef.componentInstance.assetId = assetId;
        dialogDMVRef.componentInstance.fundId = fundId;
        dialogDMVRef.componentInstance.fundReportId = fundReportId;
        dialogDMVRef.componentInstance.fundValuationId = fundValuationId;
        dialogDMVRef.componentInstance.preferredValuationType = valuationTypeEnum ?? AssetValuationTypeEnum.DiscountMultipleValuation;
        dialogDMVRef.componentInstance.fundInvestmentId = fundInvestmentId;
        dialogDMVRef.componentInstance.dealId = this.dealId;

        dialogDMVRef.componentInstance.expanded = expanded;
        dialogDMVRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((didMutate?: boolean) => {
            if (!didMutate) return;
            this.store.dispatch(FundActions.loadFundData({
                fundId,
                fundReportId,
                fundValuationId,
                dealId: this.dealId
            }));
        });
    }

    routeToAssetDetails(
        assetId: number,
        fundId: number,
        fundReportId: number,
        fundValuationId: number,
        fundInvestmentId: number,
    ) {
        this.router.navigate(["../../assets/" + assetId], {
            queryParams: {
                fundId,
                fundReportId,
                fundValuationId,
                fundInvestmentId,
                dealId: this.dealId
            }
        });
    }

    // ============================================================================
    //  Column Picker Logic
    //  --------------------
    //  Handles dynamic column selection, grouping, and expansion of scenario tokens
    //  into concrete Low/Base/High columns for the table.
    // ============================================================================

    pickerGroups: Array<{ label: string; cols: string[] }> = [];
    selectedForPicker: string[] = []; // mix of real column ids and scenario tokens
    private defaultTokensForContext: string[] = [];
    private availableColumnsForContext: string[] = [];

    // Saved tokens that the user has chosen but that are not picker-visible in the current
    // context (e.g. scenario tokens when no valuation is loaded). Kept on the side so the
    // save flow can preserve them — picking columns in one context never silently drops
    // tokens that belong to another context.
    private tokensHiddenInThisContext: string[] = [];

    private static readonly COL_GROUP: Record<string, string> = {
        company: "Company",
        ...Object.fromEntries(COMMON_COLUMNS.map(c => [c, "Company"])),
        ...Object.fromEntries(GP_REPORT_COLUMNS.map(c => [c, "GP Report"])),
        ...Object.fromEntries(VALUATION_COLUMNS.map(c => [c, "Forecast"])),
        ...Object.fromEntries(Object.values(SCENARIO_TOKEN_MEMBERS).flat().map(c => [c, "Scenarios"]))
    };

    get allGroupableCols(): string[] {
        return Object.keys(FundPartnershipInvestmentsComponent.COL_GROUP);
    }

    displayedColumnsList: string[] = [];
    groupHeaderColIds: string[] = [];
    readonly groupStartCols = new Set<string>();
    private readonly groupLabelCols = new Set<string>();

    getGroupLabel(col: string): string {
        return FundPartnershipInvestmentsComponent.COL_GROUP[col] ?? "";
    }

    isGroupLabel(col: string): boolean {
        return this.groupLabelCols.has(col);
    }

    isGroupStart(col: string): boolean {
        return this.groupStartCols.has(col);
    }

    getGroupCssClass(col: string): string {
        const group = FundPartnershipInvestmentsComponent.COL_GROUP[col] ?? "";
        return "grp-" + group.toLowerCase().replace(" ", "-");
    }

    private static readonly COL_LABEL: Record<string, string> = {
        // company (basic identification, kept tight)
        activityIndustry: "Activity/Industry",
        country: "Country",
        gic: "GIC",
        gpIndication: "GP Indication",
        investmentDate: "Inv. Date",
        percentageHeld: "% held",
        stabilityOfCFs: "Stability of CFs",
        // gp report (NAV / performance / capital)
        remainingCost: "Remaining Cost",
        gpNAV: "GP NAV",
        percentageOfTotalNAV: "% of total NAV",
        unrealizedTVPI: "Unrealized TVPI",
        totalTVPI: "Total TVPI",
        realizedTVPI: "Realized TVPI",
        realizedCost: "Realized Cost",
        realizedGains: "Realized Gains",
        totalRealized: "Total Realized",
        totalCost: "Total Cost",
        totalIRR: "Total IRR",
        realizedPercentageOfInvestment: "Real. % of Inv.",
        committedInvestment: "Committed Inv.",
        capexDrawdown: "Capex Drawdown",
        mezzanine: "Mezzanine",
        preferredEquity: "Preferred Equity",
        commonEquity: "Common Equity",
        ebitdaMargin: "Margin",
        // forecast (valuation-driven analytical metrics)
        valuationType: "Valuation Type",
        exitDateAssumption: "Exit Date Assumption",
        discountRate: "Discount Rate",
        discountToNAV: "Discount to NAV",
        selectedBid: "Selected Bid",
        percentageOfTotalBid: "% of total Bid",
        evOverEbitda: "EV/EBITDA",
        evOverRevenue: "EV/Revenue",
        impliedTrading: "GP-implied EV/EBITDA",
        revenueCAGR: "Rev CAGR",
        marginExpansion: "EBITDA Margin exp.",
        weightedRemainingHold: "Weighted Rem. Hold",
        vsComps: "vs. Comps",
        percentageOfProceeds: "% Proceeds",
        // scenarios (meta tokens)
        scenarioExitCF: "Exit CF",
        scenarioNavMultiple: "NAV Multiple",
        scenarioPrimaryTVPI: "Primary TVPI",
        scenarioSecondaryTVPI: "Secondary TVPI",
        scenarioSecondaryIRR: "Secondary IRR",
    };

    private static readonly SCENARIO_TOKENS_ORDER = SCENARIO_TOKENS_ORDER;
    private static readonly SCENARIO_MAP: Record<string, readonly string[]> = SCENARIO_TOKEN_MEMBERS;

    // Set of columns that mark the start of each scenario block (for left border)
    private readonly scenarioStartCols = new Set<string>();

    isScenarioStart(col: string) {
        return this.scenarioStartCols.has(col);
    }

    // Expands selected tokens → actual columns, preserves logical order, sets borders
    applyColumns(tokens: string[]) {
        const selectedTokens = new Set(tokens);

        const nonScenario: string[] = [];
        const lowBlock: string[] = [];
        const baseBlock: string[] = [];
        const highBlock: string[] = [];

        // Non-scenario items: keep order of selection
        for (const t of tokens) if (!FundPartnershipInvestmentsComponent.SCENARIO_MAP[t]) nonScenario.push(t);

        // For each scenario token (in fixed order), expand into low/base/high blocks
        for (const tok of FundPartnershipInvestmentsComponent.SCENARIO_TOKENS_ORDER) {
            if (!selectedTokens.has(tok)) continue;
            const [low, base, high] = FundPartnershipInvestmentsComponent.SCENARIO_MAP[tok];
            lowBlock.push(low);
            baseBlock.push(base);
            highBlock.push(high);
        }

        const ordered = ["company", ...nonScenario, ...lowBlock, ...baseBlock, ...highBlock];

        // Compute which columns get the left border
        this.scenarioStartCols.clear();
        const markStart = (block: string[]) => {
            if (block.length) this.scenarioStartCols.add(block[0]);
        };
        markStart(lowBlock);
        markStart(baseBlock);
        markStart(highBlock);

        this.displayedColumnsList = ordered;
        this.groupHeaderColIds = ordered.map(c => c + "__grp");

        this.groupStartCols.clear();
        const colGroup = FundPartnershipInvestmentsComponent.COL_GROUP;
        for (let i = 1; i < ordered.length; i++) {
            const group = colGroup[ordered[i]];
            const previousGroup = colGroup[ordered[i - 1]];
            if (group !== previousGroup && group !== "Company") {
                this.groupStartCols.add(ordered[i]);
            }
        }

        const groupColsMap = new Map<string, string[]>();
        for (const col of ordered) {
            const group = FundPartnershipInvestmentsComponent.COL_GROUP[col];
            if (!group) continue;
            if (!groupColsMap.has(group)) groupColsMap.set(group, []);
            groupColsMap.get(group)!.push(col);
        }
        this.groupLabelCols.clear();
        for (const [group, cols] of groupColsMap) {
            if (group !== "Company") {
                this.groupLabelCols.add(cols[0]);
            }
        }

        this.displayedColumns$.next(ordered);
        this.footerColsTUA = this.withSuffix(ordered, "tua");
        this.footerColsNCA = this.withSuffix(ordered, "nca");
        this.footerColsGPCR = this.withSuffix(ordered, "gpcr");
        this.footerColsBPA = this.withSuffix(ordered, "bpa");
        this.footerColsTOTAL = this.withSuffix(ordered, "total");

        this.scheduleWireSort();
    }

    private buildPickerGroupsFrom(availableCols: string[]) {
        const available = new Set(availableCols);
        const only = (xs: readonly string[]) => xs.filter(c => available.has(c));

        const common = only(COMMON_COLUMNS);
        const gpReport = only(GP_REPORT_COLUMNS);
        const valuation = only(VALUATION_COLUMNS);
        const scenarioTokens = SCENARIO_TOKENS_ORDER
            .filter(token => SCENARIO_TOKEN_MEMBERS[token].some(m => available.has(m)));

        this.pickerGroups = [
            ...(common.length ? [{label: "Company", cols: common}] : []),
            ...(gpReport.length ? [{label: "GP Report", cols: gpReport}] : []),
            ...(valuation.length ? [{label: "Forecast", cols: valuation}] : []),
            ...(scenarioTokens.length ? [{label: "Scenarios", cols: [...scenarioTokens]}] : []),
        ];
    }

    private static readonly TABLE_KEY = "fund-partnership-investments";
    private settingsApplied = false;
    private savedSort?: TableSort;
    private savedPageSize = 20;

    private initColumnPicker(availableCols: string[], defaultTokens: string[]) {
        this.defaultTokensForContext = defaultTokens;
        this.availableColumnsForContext = availableCols;
        this.buildPickerGroupsFrom(availableCols);

        if (!this.settingsApplied) {
            this.settingsApplied = true;
            this.subscriptions.push(
                this.store.select(selectSettingsLoaded).pipe(
                    filter(Boolean),
                    take(1)
                ).subscribe(() => {
                    this.store.select(selectTableColumns(FundPartnershipInvestmentsComponent.TABLE_KEY)).pipe(
                        take(1)
                    ).subscribe(savedTokens => {
                        this.applySavedOrDefaultTokens(savedTokens);
                    });

                    this.store.select(selectTableSort(FundPartnershipInvestmentsComponent.TABLE_KEY)).pipe(
                        take(1)
                    ).subscribe(savedSort => {
                        if (savedSort) {
                            this.savedSort = savedSort;
                            // wireSort picks up savedSort on its next pass via scheduleWireSort.
                            this.scheduleWireSort();
                        }
                    });

                    this.store.select(selectTablePageSize(FundPartnershipInvestmentsComponent.TABLE_KEY)).pipe(
                        take(1)
                    ).subscribe(savedPageSize => {
                        if (savedPageSize) {
                            this.savedPageSize = savedPageSize;
                        }
                    });
                })
            );
        } else {
            // Context changed (e.g. valuation loaded/cleared) — re-derive selectedForPicker
            // from what's currently saved so newly available columns appear if defaults call for it.
            this.store.select(selectTableColumns(FundPartnershipInvestmentsComponent.TABLE_KEY)).pipe(
                take(1)
            ).subscribe(savedTokens => this.applySavedOrDefaultTokens(savedTokens));
        }
    }

    private applySavedOrDefaultTokens(savedTokens: string[] | undefined) {
        const pickerVisible = new Set(this.pickerGroups.flatMap(g => g.cols));
        const allSaved = savedTokens ?? [];
        const visibleFromSaved = allSaved.filter(t => pickerVisible.has(t));
        this.tokensHiddenInThisContext = allSaved.filter(t => !pickerVisible.has(t));
        this.selectedForPicker = visibleFromSaved.length > 0
            ? visibleFromSaved
            : [...this.defaultTokensForContext];
        this.applyColumns(this.selectedForPicker);
    }

    openTableSettings() {
        const s = this.sortRef;
        const currentSort: TableSort = s?.active
            ? {field: s.active, direction: s.direction === "desc" ? "desc" : "asc"}
            : (this.savedSort ?? {field: "company", direction: "asc"});

        const dialogData: TableSettingsDialogData = {
            tableKey: FundPartnershipInvestmentsComponent.TABLE_KEY,
            columnGroups: this.pickerGroups,
            columnLabels: FundPartnershipInvestmentsComponent.COL_LABEL,
            currentColumns: this.selectedForPicker,
            currentPageSize: this.savedPageSize,
            currentSort,
            pageSizeOptions: [20],
            sortableColumns: [],   // populated below from context
            isAdmin: false,
            hidePageSize: true,
            defaultColumns: [...this.defaultTokensForContext],
            defaultPageSize: 20,
            defaultSort: {field: "company", direction: "asc"},
        };

        function getSortLabel(col: string): string {
            return FundPartnershipInvestmentsComponent.COL_LABEL[col] ?? col;
        }

        const sortableCandidates = ["company", "activityIndustry", "country", "investmentDate",
            "remainingCost", "gpNAV", "selectedBid"];
        const available = new Set(this.availableColumnsForContext);
        dialogData.sortableColumns = sortableCandidates
            .filter(c => c === "company" || available.has(c))
            .map(c => ({value: c, label: getSortLabel(c)}));

        this.dialog.open(TableSettingsDialogComponent, {data: dialogData, width: "520px"})
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result: TableSettingsDialogResult) => this.handleDialogResult(result));
    }

    private handleDialogResult(result: TableSettingsDialogResult) {
        if (!result) return;

        const tableKey = FundPartnershipInvestmentsComponent.TABLE_KEY;

        this.selectedForPicker = result.columns;
        this.applyColumns(result.columns);
        this.savedPageSize = result.pageSize;
        this.savedSort = result.sort;
        if (this.sortRef?.sortables.has(result.sort.field)) {
            this.sortRef.sort({id: result.sort.field, start: result.sort.direction as "asc" | "desc", disableClear: true});
        }

        // Preserve tokens that aren't picker-visible in the current context, so picking
        // columns here doesn't silently drop tokens chosen in another context (e.g. scenarios
        // when no valuation is loaded).
        // Deduplicate via Set to guard against race-conditions where a token in
        // tokensHiddenInThisContext is also picked in result.columns (context flip
        // between dialog open and save).
        const persistedColumns = [...new Set([...this.tokensHiddenInThisContext, ...result.columns])];
        const payload = {tableKey, columns: persistedColumns, pageSize: result.pageSize, sort: result.sort};

        if (result.saveAdmin) {
            this.store.dispatch(UserSettingActions.saveAdminTableSettings(payload));
        }
        if (result.saveUser) {
            this.store.dispatch(UserSettingActions.saveTableSettings(payload));
        }
        if (!result.saveUser && !result.saveAdmin) {
            this.store.dispatch(UserSettingActions.applyTableSettings(payload));
        }
    }

    labelFor(colOrToken: string) {
        return FundPartnershipInvestmentsComponent.COL_LABEL[colOrToken] ?? colOrToken;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
